// src/utils/gdprUtils.js

/**
 * GDPR/PDPA compliant utilities for data masking and safe logging
 */

/**
 * Mask email address for logging/display
 * @param {string} email - Email to mask
 * @returns {string} Masked email
 */
export const maskEmail = (email) => {
  if (!email || !email.includes("@")) return "[redacted]";
  const [user, domain] = email.split("@");
  const head = user.slice(0, 1);
  return `${head}${"*".repeat(Math.max(user.length - 1, 1))}@${domain}`;
};

/**
 * Mask phone number for logging/display
 * @param {string} phone - Phone number to mask
 * @returns {string} Masked phone number
 */
export const maskPhone = (phone) => {
  if (!phone) return "[redacted]";
  // Keep last 2 digits visible
  return phone.replace(/\d(?=\d{2})/g, "*");
};

/**
 * Mask personal name for logging
 * @param {string} name - Name to mask
 * @returns {string} Masked name
 */
export const maskName = (name) => {
  if (!name) return "[redacted]";
  const parts = name.split(' ');
  return parts.map(part =>
    part.length > 1 ? part[0] + '*'.repeat(part.length - 1) : part
  ).join(' ');
};

/**
 * Patterns that indicate potential PII in strings
 */
const PII_PATTERNS = [
  /@/,                    // Email addresses
  /\d{6,}/,              // Long numbers (phone, ID, etc.)
  /\b\d{4}[-\s]?\d{4}[-\s]?\d{4}[-\s]?\d{4}\b/, // Credit card patterns
  /\b\d{3}-\d{2}-\d{4}\b/, // SSN patterns
];

/**
 * Safe logging wrapper that prevents PII from being logged
 * @param {...any} args - Arguments to log
 */
export const safeLog = (...args) => {
  const stringified = JSON.stringify(args);

  // Check if any PII patterns are present
  if (PII_PATTERNS.some(pattern => pattern.test(stringified))) {
    console.warn("🔒 Log suppressed: potential PII detected");
    return;
  }

  console.log(...args);
};

/**
 * Safe error logging that masks PII in error messages
 * @param {string} message - Error message
 * @param {Error} error - Error object
 * @param {Object} context - Additional context (will be sanitized)
 */
export const safeLogError = (message, error, context = {}) => {
  // Sanitize context object
  const sanitizedContext = Object.keys(context).reduce((acc, key) => {
    const value = context[key];
    if (typeof value === 'string') {
      // Check if value looks like PII
      if (value.includes('@')) {
        acc[key] = maskEmail(value);
      } else if (/^\d+$/.test(value) && value.length > 5) {
        acc[key] = maskPhone(value);
      } else {
        acc[key] = value;
      }
    } else {
      acc[key] = value;
    }
    return acc;
  }, {});

  console.error(message, {
    error: error.message,
    stack: error.stack,
    context: sanitizedContext
  });
};

/**
 * Create audit log entry for admin actions
 * @param {string} action - Action performed
 * @param {string} actorId - ID of user performing action
 * @param {string} targetType - Type of target (appointmentRequest, etc.)
 * @param {string} targetId - ID of target
 * @param {Object} metadata - Additional metadata (will be sanitized)
 */
export const createAuditLog = (action, actorId, targetType, targetId, metadata = {}) => {
  return {
    action,
    actorId,
    targetType,
    targetId,
    timestamp: new Date().toISOString(),
    metadata: {
      ...metadata,
      // Mask any PII in metadata
      clientEmail: metadata.clientEmail ? maskEmail(metadata.clientEmail) : undefined,
      clientPhone: metadata.clientPhone ? maskPhone(metadata.clientPhone) : undefined,
      clientName: metadata.clientName ? maskName(metadata.clientName) : undefined,
    }
  };
};

/**
 * Sanitize object for safe storage/transmission
 * @param {Object} obj - Object to sanitize
 * @param {Array<string>} sensitiveFields - Fields to mask
 * @returns {Object} Sanitized object
 */
export const sanitizeObject = (obj, sensitiveFields = ['email', 'phone', 'name']) => {
  const sanitized = { ...obj };

  sensitiveFields.forEach(field => {
    if (sanitized[field]) {
      if (field === 'email') {
        sanitized[field] = maskEmail(sanitized[field]);
      } else if (field === 'phone') {
        sanitized[field] = maskPhone(sanitized[field]);
      } else if (field === 'name') {
        sanitized[field] = maskName(sanitized[field]);
      }
    }
  });

  return sanitized;
};

/**
 * Check if data retention period has expired
 * @param {Date} createdAt - When the data was created
 * @param {number} retentionMonths - Retention period in months
 * @returns {boolean} True if data should be purged
 */
export const shouldPurgeData = (createdAt, retentionMonths = 24) => {
  const now = new Date();
  const retentionDate = new Date(createdAt);
  retentionDate.setMonth(retentionDate.getMonth() + retentionMonths);

  return now > retentionDate;
};

/**
 * Generate consent text for booking forms
 * @param {string} clinicName - Name of the clinic
 * @param {string} privacyUrl - URL to privacy policy
 * @param {string} termsUrl - URL to terms of service
 * @returns {string} Consent text
 */
export const generateConsentText = (clinicName, privacyUrl, termsUrl) => {
  return `By submitting this booking request, you consent to ${clinicName} processing your personal data (name, email, phone) for scheduling and appointment-related communications. View our Privacy Policy and Terms of Service for more information.`;
};
